import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';

export interface TaskingBotCategory {
  id: string;
  userId: number;
  name: string;
  position: number;
  createdAt: string | null;
}

const QUERY_KEY = ['taskingbot', 'categories'];

export function useTaskingBotCategories() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<TaskingBotCategory[]> => {
      const res = await fetch('/_api/taskingbot/categories', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch categories');
      const data = superjson.parse<{ categories: TaskingBotCategory[] }>(await res.text());
      return data.categories;
    },
    staleTime: 30000,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string }) => {
      const res = await fetch('/_api/taskingbot/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Failed to create category';
        try { msg = superjson.parse<{ error: string }>(text).error || msg; } catch {}
        throw new Error(msg);
      }
      return superjson.parse<{ category: TaskingBotCategory }>(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { categoryId: string; name?: string; position?: number }) => {
      const res = await fetch('/_api/taskingbot/categories/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to update category');
      return superjson.parse<{ category: TaskingBotCategory }>(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (categoryId: string) => {
      const res = await fetch('/_api/taskingbot/categories/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify({ categoryId }),
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Failed to delete category';
        try { msg = superjson.parse<{ error: string }>(text).error || msg; } catch {}
        throw new Error(msg);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
